import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Button } from '@monolith/components/0-atoms/Button';
import Icon from '@monolith/components/0-atoms/Icon';
import { InfoBox } from '@opus1/harmony';
import { STAFF_IMPORT_PAGE_COPY } from '../constants/staffImport';
import { createEmptyStaffImportGridRow } from '../helpers/staffImportGridMapping';
import { staffImportTokens } from '../styles/staffImportTokens';
import { ImportGridColumn, ImportGridEditor } from './ImportGridEditor';
import {
    STAFF_IMPORT_BUILTIN_ROLE_OPTIONS,
    StaffImportGridColumnKey,
    StaffImportGridRow,
    DUPLICATE_EMAIL_IN_BUSINESS_MESSAGE,
    ValidationIssue,
    validateStaffImportGridRow,
} from '../types/staffImport';

type StaffImportGridColumn = ImportGridColumn<StaffImportGridRow> & {
    width: string;
    editor: 'text' | 'role';
    subheader?: string;
};

export const STAFF_IMPORT_COLUMNS: StaffImportGridColumn[] = [
    {
        key: 'firstName',
        header: 'First Name*',
        required: true,
        width: '120px',
        editor: 'text',
    },
    {
        key: 'middleName',
        header: 'Middle Name',
        width: '120px',
        editor: 'text',
    },
    {
        key: 'lastName',
        header: 'Last Name*',
        required: true,
        width: '120px',
        editor: 'text',
    },
    {
        key: 'staffRole',
        header: 'Staff Role*',
        required: true,
        width: '170px',
        editor: 'role',
    },
    {
        key: 'email',
        header: 'Email',
        subheader: STAFF_IMPORT_PAGE_COPY.emailColumnSubheader,
        width: '240px',
        editor: 'text',
    },
    {
        key: 'employeeId',
        header: 'Employee ID',
        width: '140px',
        editor: 'text',
    },
    { key: 'gender', header: 'Gender', width: '120px', editor: 'text' },
    {
        key: 'streetAddress',
        header: 'Street Address',
        width: '200px',
        editor: 'text',
    },
    { key: 'city', header: 'City', width: '160px', editor: 'text' },
    { key: 'state', header: 'State', width: '120px', editor: 'text' },
    { key: 'zip', header: 'Zip', width: '120px', editor: 'text' },
    { key: 'country', header: 'Country', width: '90px', editor: 'text' },
    { key: 'birthday', header: 'Birthday', width: '120px', editor: 'text' },
    {
        key: 'primaryPhone',
        header: 'Primary Phone',
        width: '140px',
        editor: 'text',
    },
    {
        key: 'onlineLink',
        header: 'Online video conference link',
        width: '260px',
        editor: 'text',
    },
];

export interface StaffImportGridProps {
    rows: StaffImportGridRow[];
    issues?: ValidationIssue[];
    existingBusinessEmails?: string[];
    disabled?: boolean;
    disabledMessage?: string;
    onRowsChange: (rows: StaffImportGridRow[]) => void;
    className?: string;
}

const GRID_CELL_EDITOR_SELECTOR = 'input, select, textarea';

const handleStaffImportGridKeyDown = (
    event: React.KeyboardEvent<HTMLTableSectionElement>,
) => {
    if (event.key !== 'Enter') {
        return;
    }

    const origin = event.target;
    if (
        !(origin instanceof HTMLElement) ||
        !origin.matches(GRID_CELL_EDITOR_SELECTOR)
    ) {
        return;
    }

    if (event.nativeEvent.isComposing) {
        return;
    }

    event.preventDefault();

    const row = origin.closest('tr');
    const nextRow = row?.nextElementSibling;
    if (!row || !nextRow) {
        return;
    }

    const editors = Array.from(
        row.querySelectorAll<HTMLElement>(GRID_CELL_EDITOR_SELECTOR),
    );
    const columnIndex = editors.indexOf(origin);
    if (columnIndex < 0) {
        return;
    }

    nextRow
        .querySelectorAll<HTMLElement>(GRID_CELL_EDITOR_SELECTOR)
        [columnIndex]?.focus();
};

const GridSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${staffImportTokens.spacing.sectionGap}px;
    min-width: 0;
    width: 100%;
`;

const ValidationIntro = styled.p`
    margin: 0;
    color: ${staffImportTokens.colors.red9};
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-weight: ${staffImportTokens.typography.bodyDefault.fontWeight};
    line-height: ${staffImportTokens.typography.bodyDefault.lineHeight};
`;

const TableWrapper = styled.div<{ $disabled?: boolean }>`
    box-sizing: border-box;
    width: 100%;
    overflow-x: auto;
    background: #ffffff;
    opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'auto')};
`;

const GridTable = styled.table`
    border-collapse: collapse;
    min-width: 1900px;
    width: 100%;
    font-family: 'Montserrat', sans-serif;
`;

const HeaderCell = styled.th<{ $width?: string }>`
    border: 1px solid ${staffImportTokens.colors.grey5};
    background: #ffffff;
    color: ${staffImportTokens.colors.textPrimary};
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-weight: 700;
    line-height: 1.2;
    padding: 8px;
    text-align: left;
    width: ${({ $width }) => $width || 'auto'};
    vertical-align: bottom;
`;

const StackedHeaderTitle = styled.span`
    display: block;
`;

const StackedHeaderSubtext = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    color: ${staffImportTokens.colors.textDefault};
`;

const ActionHeaderCell = styled(HeaderCell)`
    width: 48px;
    text-align: center;
`;

const NumberHeaderCell = styled(HeaderCell)`
    width: 30px;
    text-align: center;
`;

const DataCell = styled.td<{ $state: 'default' | 'error' | 'warning' }>`
    border: 1px solid ${staffImportTokens.colors.grey5};
    background-color: ${({ $state }) => {
        if ($state === 'error') {
            return staffImportTokens.colors.red3;
        }
        if ($state === 'warning') {
            return staffImportTokens.colors.amber2;
        }
        return '#ffffff';
    }};
    padding: 0;
`;

const ActionCell = styled.td`
    border: 1px solid ${staffImportTokens.colors.grey5};
    text-align: center;
    width: 48px;
    background: #ffffff;
`;

const ActionCellContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
`;

const RowNumberCell = styled.td`
    border: 1px solid ${staffImportTokens.colors.grey5};
    width: 30px;
    text-align: center;
    color: ${staffImportTokens.colors.textPrimary};
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-weight: ${staffImportTokens.typography.bodyDefault.fontWeight};
    background: #ffffff;
`;

const InlineInput = styled.input<{ $state?: 'default' | 'error' | 'warning' }>`
    width: 100%;
    border: none;
    background: transparent;
    box-sizing: border-box;
    color: ${({ $state }) =>
        $state === 'error'
            ? staffImportTokens.colors.red9
            : staffImportTokens.colors.textPrimary};
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-weight: ${staffImportTokens.typography.bodyDefault.fontWeight};
    line-height: 1.2;
    padding: 16px 8px;

    &:focus {
        outline: 2px solid ${staffImportTokens.colors.primary};
        outline-offset: -2px;
    }
`;

const RoleCellWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const InlineSelect = styled.select<{
    $state?: 'default' | 'error' | 'warning';
}>`
    width: 100%;
    border: none;
    background: transparent;
    box-sizing: border-box;
    appearance: none;
    color: ${({ $state }) =>
        $state === 'error'
            ? staffImportTokens.colors.red9
            : staffImportTokens.colors.textPrimary};
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-weight: ${staffImportTokens.typography.bodyDefault.fontWeight};
    line-height: 1.2;
    padding: 16px 28px 16px 8px;

    &:focus {
        outline: 2px solid ${staffImportTokens.colors.primary};
        outline-offset: -2px;
    }
`;

const RoleChevron = styled.span`
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    pointer-events: none;
    color: ${staffImportTokens.colors.textPrimary};
`;

const RemoveButton = styled.button`
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${staffImportTokens.colors.red9};
    padding: 0;
`;

const DisabledMessageCell = styled.td`
    border: 1px solid ${staffImportTokens.colors.grey5};
    background: #ffffff;
    color: ${staffImportTokens.colors.textDefault};
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-style: italic;
    font-weight: ${staffImportTokens.typography.bodyDefault.fontWeight};
    line-height: ${staffImportTokens.typography.bodyDefault.lineHeight};
    padding: 32px 16px;
    text-align: center;
`;

const STAFF_IMPORT_GRID_COLUMN_COUNT = STAFF_IMPORT_COLUMNS.length + 2;

const Legend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${staffImportTokens.spacing.sectionGap}px;
    align-items: center;
    color: ${staffImportTokens.colors.textDefault};
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.legend.fontSize};
    font-style: italic;
    font-weight: ${staffImportTokens.typography.legend.fontWeight};
    line-height: ${staffImportTokens.typography.legend.lineHeight};
`;

const LegendSwatch = styled.span<{
    $borderColor: string;
    $fillColor: string;
}>`
    box-sizing: border-box;
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid ${({ $borderColor }) => $borderColor};
    background: ${({ $fillColor }) => $fillColor};
    flex-shrink: 0;
`;

const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const renderColumnHeader = (column: StaffImportGridColumn) => {
    if (column.subheader) {
        return (
            <HeaderCell key={column.key} $width={column.width}>
                <StackedHeaderTitle>{column.header}</StackedHeaderTitle>
                <StackedHeaderSubtext>{column.subheader}</StackedHeaderSubtext>
            </HeaderCell>
        );
    }

    return (
        <HeaderCell key={column.key} $width={column.width}>
            {column.header}
        </HeaderCell>
    );
};

const AddRowActions = styled.div`
    display: flex;
    justify-content: flex-start;
`;

export const StaffImportGrid: React.FC<StaffImportGridProps> = ({
    rows,
    issues = [],
    existingBusinessEmails = [],
    disabled = false,
    disabledMessage,
    onRowsChange,
    className,
}) => {
    const duplicateEmails = useMemo(() => {
        const counts = rows.reduce<Record<string, number>>((result, row) => {
            const email = row.email.trim().toLowerCase();
            if (email) {
                result[email] = (result[email] || 0) + 1;
            }
            return result;
        }, {});
        return new Set(
            Object.entries(counts)
                .filter(([, count]) => count > 1)
                .map(([email]) => email),
        );
    }, [rows]);
    const existingEmails = useMemo(
        () =>
            new Set(
                existingBusinessEmails.map((email) =>
                    email.trim().toLowerCase(),
                ),
            ),
        [existingBusinessEmails],
    );
    const etlIssuesByRow = useMemo(() => {
        const result = new Map<
            string,
            Partial<Record<StaffImportGridColumnKey, string>>
        >();
        issues.forEach((issue) => {
            const rowId = `row-${issue.rowNumber - 1}`;
            const field = issue.field as StaffImportGridColumnKey;
            const rowIssues = result.get(rowId) || {};
            rowIssues[field] = issue.message;
            result.set(rowId, rowIssues);
        });
        return result;
    }, [issues]);

    if (disabled) {
        return (
            <GridSection className={className}>
                <TableWrapper $disabled aria-disabled="true">
                    <GridTable>
                        <thead>
                            <tr>
                                <ActionHeaderCell>&nbsp;</ActionHeaderCell>
                                <NumberHeaderCell>#</NumberHeaderCell>
                                {STAFF_IMPORT_COLUMNS.map((column) =>
                                    renderColumnHeader(column),
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <DisabledMessageCell
                                    colSpan={STAFF_IMPORT_GRID_COLUMN_COUNT}
                                >
                                    {disabledMessage ??
                                        STAFF_IMPORT_PAGE_COPY.gridDisabledMessage}
                                </DisabledMessageCell>
                            </tr>
                        </tbody>
                    </GridTable>
                </TableWrapper>
            </GridSection>
        );
    }

    return (
        <GridSection className={className}>
            <ImportGridEditor
                columns={STAFF_IMPORT_COLUMNS}
                rows={rows}
                getRowId={(row) => row.id}
                validateRow={(row) => {
                    const validation = validateStaffImportGridRow(row);
                    Object.entries(etlIssuesByRow.get(row.id) || {}).forEach(
                        ([field, message]) => {
                            validation.cellErrors[
                                field as StaffImportGridColumnKey
                            ] = message;
                        },
                    );
                    if (duplicateEmails.has(row.email.trim().toLowerCase())) {
                        validation.cellErrors.email =
                            'Email must be unique within this import.';
                    } else if (
                        existingEmails.has(row.email.trim().toLowerCase())
                    ) {
                        validation.cellErrors.email =
                            DUPLICATE_EMAIL_IN_BUSINESS_MESSAGE;
                    }
                    return validation;
                }}
                onRowsChange={onRowsChange}
            >
                {({
                    columns,
                    rows: currentRows,
                    getRowId,
                    getVisibleRowNumber,
                    getCellError,
                    getCellWarning,
                    removeRow,
                    appendRow,
                    updateCell,
                    summary,
                }) => (
                    <>
                        {summary.hasBlockingErrors ? (
                            <ValidationIntro>
                                {
                                    STAFF_IMPORT_PAGE_COPY.gridValidationIntroMessage
                                }
                            </ValidationIntro>
                        ) : null}
                        <TableWrapper>
                            <GridTable>
                                <thead>
                                    <tr>
                                        <ActionHeaderCell>
                                            &nbsp;
                                        </ActionHeaderCell>
                                        <NumberHeaderCell>#</NumberHeaderCell>
                                        {columns.map((column) =>
                                            renderColumnHeader(
                                                column as StaffImportGridColumn,
                                            ),
                                        )}
                                    </tr>
                                </thead>
                                <tbody onKeyDown={handleStaffImportGridKeyDown}>
                                    {currentRows.map((row) => {
                                        const rowId = getRowId(row);
                                        const visibleRowNumber =
                                            getVisibleRowNumber(rowId) ?? 0;
                                        return (
                                            <tr key={row.id}>
                                                <ActionCell>
                                                    <ActionCellContent>
                                                        <RemoveButton
                                                            type="button"
                                                            onClick={() =>
                                                                removeRow(rowId)
                                                            }
                                                            aria-label={`Remove row ${visibleRowNumber}`}
                                                        >
                                                            <Icon
                                                                color="red"
                                                                fasIcon="circle-minus"
                                                            />
                                                        </RemoveButton>
                                                    </ActionCellContent>
                                                </ActionCell>
                                                <RowNumberCell>
                                                    {visibleRowNumber}
                                                </RowNumberCell>
                                                {columns.map((column) => {
                                                    const columnKey =
                                                        column.key;
                                                    const value =
                                                        row[columnKey];
                                                    const cellError =
                                                        getCellError(
                                                            rowId,
                                                            columnKey,
                                                        );
                                                    const cellWarning =
                                                        getCellWarning(
                                                            rowId,
                                                            columnKey,
                                                        );

                                                    const cellState =
                                                        cellError != null
                                                            ? 'error'
                                                            : cellWarning !=
                                                                null
                                                              ? 'warning'
                                                              : 'default';

                                                    return (
                                                        <DataCell
                                                            key={columnKey}
                                                            $state={cellState}
                                                            data-cell-state={
                                                                cellState
                                                            }
                                                            title={
                                                                cellError ||
                                                                cellWarning
                                                            }
                                                        >
                                                            {(
                                                                column as StaffImportGridColumn
                                                            ).editor ===
                                                            'role' ? (
                                                                <RoleCellWrapper>
                                                                    <InlineSelect
                                                                        value={
                                                                            value
                                                                        }
                                                                        $state={
                                                                            cellState
                                                                        }
                                                                        onChange={(
                                                                            event,
                                                                        ) =>
                                                                            updateCell(
                                                                                rowId,
                                                                                columnKey,
                                                                                event
                                                                                    .target
                                                                                    .value as StaffImportGridRow[typeof columnKey],
                                                                            )
                                                                        }
                                                                        aria-label={`${column.header} row ${visibleRowNumber}`}
                                                                        aria-invalid={
                                                                            cellError !=
                                                                            null
                                                                        }
                                                                    >
                                                                        <option value="">
                                                                            Select
                                                                            role
                                                                        </option>
                                                                        {STAFF_IMPORT_BUILTIN_ROLE_OPTIONS.map(
                                                                            (
                                                                                option,
                                                                            ) => (
                                                                                <option
                                                                                    key={
                                                                                        option.value
                                                                                    }
                                                                                    value={
                                                                                        option.value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        option.label
                                                                                    }
                                                                                </option>
                                                                            ),
                                                                        )}
                                                                    </InlineSelect>
                                                                    <RoleChevron aria-hidden="true">
                                                                        <Icon fasIcon="chevron-down" />
                                                                    </RoleChevron>
                                                                </RoleCellWrapper>
                                                            ) : (
                                                                <InlineInput
                                                                    type="text"
                                                                    value={
                                                                        value
                                                                    }
                                                                    $state={
                                                                        cellState
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) =>
                                                                        updateCell(
                                                                            rowId,
                                                                            columnKey,
                                                                            event
                                                                                .target
                                                                                .value as StaffImportGridRow[typeof columnKey],
                                                                        )
                                                                    }
                                                                    aria-label={`${column.header} row ${visibleRowNumber}`}
                                                                    aria-invalid={
                                                                        cellError !=
                                                                        null
                                                                    }
                                                                />
                                                            )}
                                                        </DataCell>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </GridTable>
                        </TableWrapper>
                        <AddRowActions>
                            <Button
                                id="staff-import-add-row"
                                type="button"
                                variant="secondary"
                                size="compact"
                                onClick={() =>
                                    appendRow(createEmptyStaffImportGridRow())
                                }
                            >
                                <Icon farIcon="plus" />
                                {STAFF_IMPORT_PAGE_COPY.addRowLabel}
                            </Button>
                        </AddRowActions>
                        <Legend>
                            <LegendItem>
                                <LegendSwatch
                                    $borderColor={staffImportTokens.colors.red9}
                                    $fillColor={staffImportTokens.colors.red3}
                                />
                                <span>
                                    {
                                        STAFF_IMPORT_PAGE_COPY.gridLegendBlockingLabel
                                    }
                                </span>
                            </LegendItem>
                            <span>|</span>
                            <LegendItem>
                                <LegendSwatch
                                    $borderColor="#f5a623"
                                    $fillColor={staffImportTokens.colors.amber2}
                                />
                                <span>
                                    {
                                        STAFF_IMPORT_PAGE_COPY.gridLegendOptionalLabel
                                    }
                                </span>
                            </LegendItem>
                            <span>|</span>
                            <span>* Required Values to create new staff.</span>
                            <span>|</span>
                            <span>
                                Click the values to edit in the table above.
                            </span>
                            <span>|</span>
                            <span>Scroll Left and Right for more columns</span>
                        </Legend>
                        {summary.hasBlockingErrors ? (
                            <InfoBox variant="error">
                                {STAFF_IMPORT_PAGE_COPY.gridValidationIssueCountMessage(
                                    summary.errorCount,
                                )}
                            </InfoBox>
                        ) : summary.warningCount > 0 ? (
                            <InfoBox variant="warning">
                                {STAFF_IMPORT_PAGE_COPY.gridReadyWithWarningsMessage(
                                    summary.warningCount,
                                )}
                            </InfoBox>
                        ) : (
                            <InfoBox variant="success">
                                {STAFF_IMPORT_PAGE_COPY.readyForImportMessage}
                            </InfoBox>
                        )}
                    </>
                )}
            </ImportGridEditor>
        </GridSection>
    );
};

export default StaffImportGrid;
